import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { queryKeys } from '~/shared/api/queryKeys';
import {
    adminPayoutCascadeServiceCreateCascade,
    adminPayoutCascadeServiceCreateGroup,
    adminPayoutCascadeServiceCreateTemplate,
    adminPayoutCascadeServiceDeleteCascade,
    adminPayoutCascadeServiceDeleteGroup,
    adminPayoutCascadeServiceDeleteTemplate,
    adminPayoutCascadeServiceGetCascade,
    adminPayoutCascadeServiceGetGroup,
    adminPayoutCascadeServiceGetTemplate,
    adminPayoutCascadeServiceListCascades,
    adminPayoutCascadeServiceListGroups,
    adminPayoutCascadeServiceListTemplates,
    adminPayoutCascadeServiceUpdateCascadeV2,
    adminPayoutCascadeServiceUpdateGroup,
    adminPayoutCascadeServiceUpdateTemplate,
    getAdminPayoutCascadeServiceGetCascadeUrl,
    getAdminPayoutCascadeServiceGetGroupUrl,
    getAdminPayoutCascadeServiceGetTemplateUrl,
    getAdminPayoutCascadeServiceListCascadesUrl,
    getAdminPayoutCascadeServiceListGroupsUrl,
    getAdminPayoutCascadeServiceListTemplatesUrl,
} from '~/generated/payoutBackend/cascade';
import {
    type PayoutAdminCascadeV1CreateCascadeRequest,
    type AdminPayoutCascadeServiceDeleteCascadeParams,
    type AdminPayoutCascadeServiceGetCascadeParams,
    type AdminPayoutCascadeServiceListCascadesParams,
    type AdminPayoutCascadeServiceListGroupsParams,
    type AdminPayoutCascadeServiceGetGroupParams,
    type AdminPayoutCascadeServiceDeleteGroupParams,
    type PayoutAdminCascadeV1CreateGroupRequest,
    type PayoutAdminCascadeV1UpdateGroupRequest,
    type PayoutAdminCascadeV1UpdateCascadeV2Request,
    type AdminPayoutCascadeServiceListTemplatesParams,
    type AdminPayoutCascadeServiceGetTemplateParams,
    type AdminPayoutCascadeServiceDeleteTemplateParams,
    type PayoutAdminCascadeV1CreateTemplateRequest,
    type PayoutAdminCascadeV1UpdateTemplateRequest,
} from '~/generated/payoutBackend/model';
import { isNewPayoutCascadeEntityId } from './urls';

export const useCascadesList = (params: AdminPayoutCascadeServiceListCascadesParams) => {
    return useQuery({
        refetchOnWindowFocus: true,
        queryFn: () => adminPayoutCascadeServiceListCascades(params),
        queryKey: [queryKeys.PAYOUT_CASCADES, getAdminPayoutCascadeServiceListCascadesUrl(params)],
    });
};

export const useCacade = (params: AdminPayoutCascadeServiceGetCascadeParams) => {
    return useQuery({
        refetchOnWindowFocus: true,
        queryFn: () => adminPayoutCascadeServiceGetCascade(params),
        queryKey: [queryKeys.PAYOUT_CASCADES, getAdminPayoutCascadeServiceGetCascadeUrl(params)],
        enabled: Boolean(params['id.value']) && !isNewPayoutCascadeEntityId(params['id.value']),
    });
};

interface UseCascadeMutationProps {
    onError?: (error: Error) => void;
    onSuccess?: () => void;
}

export const useDeleteCascade = (options?: UseCascadeMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: AdminPayoutCascadeServiceDeleteCascadeParams) => {
            return await adminPayoutCascadeServiceDeleteCascade(params);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_CASCADES],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Уведомление',
                        message: 'Каскад удалён',
                    });
                });
        },
    });
};

export const useCreateCascade = (options?: UseCascadeMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: PayoutAdminCascadeV1CreateCascadeRequest) => {
            return await adminPayoutCascadeServiceCreateCascade(params);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_CASCADES],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Уведомление',
                        message: 'Каскад создан',
                    });
                });
        },
    });
};

export const useUpdateCascadeV2 = (options?: UseCascadeMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: PayoutAdminCascadeV1UpdateCascadeV2Request) => {
            return await adminPayoutCascadeServiceUpdateCascadeV2(params);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_CASCADES],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Уведомление',
                        message: 'Каскад обновлён',
                    });
                });
        },
    });
};

export const useCascadeGroupsList = (params: AdminPayoutCascadeServiceListGroupsParams) => {
    return useQuery({
        refetchOnWindowFocus: true,
        queryFn: () => adminPayoutCascadeServiceListGroups(params),
        queryKey: [queryKeys.PAYOUT_CASCADE_GROUPS, getAdminPayoutCascadeServiceListGroupsUrl(params)],
    });
};

export const useCascadeGroup = (params: AdminPayoutCascadeServiceGetGroupParams) => {
    return useQuery({
        refetchOnWindowFocus: true,
        queryFn: () => adminPayoutCascadeServiceGetGroup(params),
        queryKey: [queryKeys.PAYOUT_CASCADE_GROUPS, getAdminPayoutCascadeServiceGetGroupUrl(params)],
        enabled: Boolean(params['id.value']) && !isNewPayoutCascadeEntityId(params['id.value']),
    });
};

export const useDeleteCascadeGroup = (options?: UseCascadeMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: AdminPayoutCascadeServiceDeleteGroupParams) => {
            return await adminPayoutCascadeServiceDeleteGroup(params);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_CASCADE_GROUPS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Уведомление',
                        message: 'Группа удалена',
                    });
                });
        },
    });
};

export const useCreateCascadeGroup = (options?: UseCascadeMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: PayoutAdminCascadeV1CreateGroupRequest) => {
            return await adminPayoutCascadeServiceCreateGroup(params);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_CASCADE_GROUPS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Уведомление',
                        message: 'Группа создана',
                    });
                });
        },
    });
};

export const useUpdateCascadeGroup = (options?: UseCascadeMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: PayoutAdminCascadeV1UpdateGroupRequest) => {
            return await adminPayoutCascadeServiceUpdateGroup(params);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_CASCADE_GROUPS],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Уведомление',
                        message: 'Группа обновлена',
                    });
                });
        },
    });
};

export const useCascadeTemplatesList = (params: AdminPayoutCascadeServiceListTemplatesParams) => {
    return useQuery({
        refetchOnWindowFocus: true,
        queryFn: () => adminPayoutCascadeServiceListTemplates(params),
        queryKey: [queryKeys.PAYOUT_CASCADE_TEMPLATES, getAdminPayoutCascadeServiceListTemplatesUrl(params)],
    });
};

export const useCascadeTemplate = (params: AdminPayoutCascadeServiceGetTemplateParams) => {
    return useQuery({
        refetchOnWindowFocus: true,
        queryFn: () => adminPayoutCascadeServiceGetTemplate(params),
        queryKey: [queryKeys.PAYOUT_CASCADE_TEMPLATES, getAdminPayoutCascadeServiceGetTemplateUrl(params)],
        enabled: Boolean(params['id.value']) && !isNewPayoutCascadeEntityId(params['id.value']),
    });
};

export const useDeleteCascadeTemplate = (options?: UseCascadeMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: AdminPayoutCascadeServiceDeleteTemplateParams) => {
            return await adminPayoutCascadeServiceDeleteTemplate(params);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_CASCADE_TEMPLATES],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Уведомление',
                        message: 'Шаблон удалён',
                    });
                });
        },
    });
};

export const useCreateCascadeTemplate = (options?: UseCascadeMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: PayoutAdminCascadeV1CreateTemplateRequest) => {
            return await adminPayoutCascadeServiceCreateTemplate(params);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_CASCADE_TEMPLATES],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Уведомление',
                        message: 'Шаблон создан',
                    });
                });
        },
    });
};

export const useUpdateCascadeTemplate = (options?: UseCascadeMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: PayoutAdminCascadeV1UpdateTemplateRequest) => {
            return await adminPayoutCascadeServiceUpdateTemplate(params);
        },
        onError: options?.onError,
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [queryKeys.PAYOUT_CASCADE_TEMPLATES],
                })
                .then(() => {
                    options?.onSuccess?.();
                    notifications.show({
                        color: 'green',
                        title: 'Уведомление',
                        message: 'Шаблон обновлён',
                    });
                });
        },
    });
};
